/**
 * useSessionFreshness — профиль (а с ним права) перезапрашивается ПРИ ПЕРЕХОДЕ
 * между страницами. Блок 2 · Ф8 «Права доступа» · В-О2.
 *
 * ─── Откуда требование ──────────────────────────────────────────────────────
 *
 * Р-198: отзыв права «действует со следующего перехода или обновления»,
 * сессия не рвётся, открытая вкладка не трогается. Сервер это исполняет сам —
 * он читает права из базы на каждом запросе. Веб держит профиль в памяти
 * вкладки, и до волны обновлял его только «когда придётся»: свежесть 30 секунд
 * (`staleTime`) — это НЕ таймер, а порог, после которого запрос уйдёт при
 * следующем монтировании читателя. Человек, ходящий по страницам чаще, чем раз
 * в полминуты, либо по страницам, где профиль уже смонтирован оболочкой, мог
 * не получить новые права сколь угодно долго.
 *
 * ─── Что делает ─────────────────────────────────────────────────────────────
 *
 * 1. Смена ПУТИ (`pathname`) — запрос `/auth/me` уходит сразу, свежесть не
 *    спрашивается. Смена параметров адреса (вкладка, фильтр, период) переходом
 *    не считается: это та же страница.
 * 2. Если после перехода СОСТАВ ПРАВ другой — сбрасываются все кэши, кроме
 *    сессии: данные, полученные под прежними правами, на новой странице не
 *    рисуются (кэш запросов живёт пять минут и отдаётся без запроса полминуты).
 *    Состав прежний — кэши не трогаются.
 *
 * ─── Чего НЕ делает — и это решения, а не пропуски ──────────────────────────
 *
 * · ПО ВОЗВРАТУ ФОКУСА В ОКНО НЕ ПЕРЕЗАПРАШИВАЕТ (`refetchOnWindowFocus: false`
 *   у `useMe`, явно). Возврат к вкладке — не переход; Р-217 (г): открытая
 *   вкладка «принудительно не перезагружается», а первое действие в ней
 *   получает штатный отказ сервера.
 * · ОТКРЫТУЮ СТРАНИЦУ НЕ ПЕРЕРИСОВЫВАЕТ: сброс кэшей привязан к переходу, а не
 *   к моменту, когда новые права стали известны. Сравнивается состав прав «на
 *   прошлом переходе» с составом «на этом» — поэтому смена, пришедшая между
 *   переходами (профиль перезапросил кто-то ещё), всё равно даст сброс, но на
 *   следующем переходе.
 * · НЕ РУБЕЖ. Рубеж — сервер.
 */
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { qk } from '@/lib/query-keys';
import type { MeUser } from './use-me';
import { rightsFingerprint } from './use-rights';

/** Корень ключей сессии: эти запросы сбросу не подлежат. */
const SESSION_KEY_ROOT = qk.auth.all[0];

/**
 * Вызывается ОДИН раз на оболочку защищённых маршрутов (`<RequireAuth>`):
 * она смонтирована на всё время сессии и видит каждый переход.
 */
export function useSessionFreshness(pathname: string, me: MeUser | null | undefined): void {
  const qc = useQueryClient();
  const lastPath = React.useRef(pathname);
  /** Состав прав на прошлом переходе; `null` — ещё не загружались. */
  const baseline = React.useRef<string | null>(null);

  // Первая загрузка профиля — точка отсчёта, а не «смена состава».
  const current = rightsFingerprint(me);
  React.useEffect(() => {
    if (baseline.current === null && current !== null) baseline.current = current;
  }, [current]);

  React.useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;

    let cancelled = false;
    // `cancelRefetch: false` — присоединиться к запросу профиля, который уже
    // в пути, а не отменять его и слать второй. Переход почти всегда застаёт
    // такой запрос: его шлёт читатель профиля на новой странице (если профиль
    // старше порога свежести) либо предыдущее звено цепочки редиректов
    // (`/` → домашняя страница). Без флага на один переход уходило два
    // `/auth/me`. Запрос «в пути» выпущен тем же переходом — его ответ не
    // старее того, что принёс бы второй.
    void qc
      .invalidateQueries({ queryKey: qk.auth.session() }, { cancelRefetch: false })
      .then(() => {
        if (cancelled) return;
        const fresh = rightsFingerprint(qc.getQueryData<MeUser>(qk.auth.session()));
        // ⚠️ ОТКАЗ ПЕРЕЗАПРОСА СЮДА ПРИХОДИТ НЕ КАК `null`. `invalidateQueries`
        // ошибку запроса не пробрасывает, а данные в кэше при ней остаются
        // прежними — значит `fresh` равен отпечатку ПРЕЖНЕГО профиля,
        // совпадает с отсчётом, и сброса нет. Отсчёт при этом не сдвигается:
        // когда сервер ответит (на одном из следующих переходов), смена
        // состава прав будет замечена. Проверено тестом («назад» после отказа).
        //
        // `null` здесь — другое: профиля в кэше нет вовсе (выход, сброс
        // кэша между переходом и ответом) либо его поле `rights` не
        // разобралось. Сравнивать не с чем — отсчёт остаётся прежним.
        if (fresh === null) return;
        const changed = baseline.current !== null && baseline.current !== fresh;
        baseline.current = fresh;
        if (changed) {
          void qc.resetQueries({ predicate: (query) => query.queryKey[0] !== SESSION_KEY_ROOT });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pathname, qc]);
}
